/**
 * FTT conjoint recognition-memory test, shown on its own page after a tested
 * article (the article is NOT re-shown and there is no Back button).
 *
 * Draws a constrained-random subset of the article's probe pool (always ≥1
 * verbatim and ≥1 gist item; see sampleMemoryProbes) so no fixed pattern is
 * learnable, and asks for each whether it appeared in the article. Scores
 * verbatim and gist HITS and FALSE ALARMS separately (see scoreMemoryProbes),
 * crossing trace (verbatim/gist) × veracity (present/absent).
 *
 * `role` distinguishes the first-tested article (incidental/surprise encoding)
 * from the second (intentional encoding, participant now knows a test can come).
 */
import React, { useState } from 'react';
import {
  sampleMemoryProbes,
  scoreMemoryProbes,
  MemoryProbeItem,
  MemoryItemAnswer,
  MemoryScore,
} from '../../data/memoryProbeItems';

export interface MemoryTestData extends MemoryScore {
  articleId: string;
  role: 'incidental' | 'intentional';
  items: MemoryItemAnswer[];
  startTime: number;
  completionTime: number;
}

interface Props {
  articleId: string;
  role: 'incidental' | 'intentional';
  onComplete: (data: MemoryTestData) => void;
}

const MemoryTest: React.FC<Props> = ({ articleId, role, onComplete }) => {
  // Draw + freeze the subset for this article on mount.
  const [items] = useState<MemoryProbeItem[]>(() => sampleMemoryProbes(articleId, 4));
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [startTime] = useState(Date.now());

  const allAnswered = items.length > 0 && items.every(it => answers[it.id] !== undefined);

  const handleSubmit = () => {
    if (!allAnswered) return;
    const scored: MemoryItemAnswer[] = items.map(it => ({
      id: it.id, kind: it.kind, present: it.present, answeredYes: !!answers[it.id],
    }));
    onComplete({
      articleId, role,
      items: scored,
      ...scoreMemoryProbes(scored),
      startTime, completionTime: Date.now(),
    });
  };

  return (
    <div style={{ maxWidth: '640px', margin: '40px auto', padding: '24px' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a202c', marginBottom: '8px' }}>About the article</h2>
      <p style={{ color: '#4a5568', lineHeight: 1.6, marginBottom: '24px' }}>
        For each statement below, indicate whether it reflects what the article you just read said.
      </p>

      {items.map(it => (
        <div key={it.id} style={{ marginBottom: '18px', padding: '16px', border: '1px solid #e3e6ea', borderRadius: '10px', background: '#fafbfc' }}>
          <div style={{ fontSize: '15px', color: '#2d3748', lineHeight: 1.5, marginBottom: '12px' }}>“{it.text}”</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {[{ v: true, label: 'Yes — the article said this' }, { v: false, label: 'No — it did not' }].map(opt => {
              const active = answers[it.id] === opt.v;
              return (
                <label key={String(opt.v)} onClick={() => setAnswers(prev => ({ ...prev, [it.id]: opt.v }))}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '12px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
                    fontWeight: active ? 600 : 400, color: active ? '#0066cc' : '#2d3748',
                    background: active ? '#e3f2fd' : '#fff', border: active ? '2px solid #0066cc' : '1px solid #dee2e6' }}>
                  <input type="radio" name={it.id} checked={active} onChange={() => setAnswers(prev => ({ ...prev, [it.id]: opt.v }))} style={{ margin: 0 }} />
                  {opt.label}
                </label>
              );
            })}
          </div>
        </div>
      ))}

      <div style={{ textAlign: 'right', marginTop: '24px' }}>
        <button onClick={handleSubmit} disabled={!allAnswered}
          style={{ padding: '14px 32px', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600,
            color: 'white', backgroundColor: allAnswered ? '#28a745' : '#ccc', cursor: allAnswered ? 'pointer' : 'not-allowed' }}>
          Continue
        </button>
      </div>
    </div>
  );
};

export default MemoryTest;
